import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { projectsApi } from '../lib/api';
import { useOrg } from '../contexts/OrgContext';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';

function Projects() {
  const { selectedOrgId, selectedOrg, loading: orgLoading } = useOrg();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', location: '' });
  const [editing, setEditing] = useState(null); // { id, name, description, location }
  const [deleting, setDeleting] = useState(null); // { project, needsForce, error }
  const [busy, setBusy] = useState(false);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const projectsRes = await projectsApi.list(selectedOrgId);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setProjectsLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedOrgId) {
      setProjects([]);
      setProjectsLoading(false);
      return;
    }
    loadProjects();
  }, [selectedOrgId, loadProjects]);

  const loading = orgLoading || projectsLoading;

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      await projectsApi.create(newProject, selectedOrg.id);
      setShowCreateForm(false);
      setNewProject({ name: '', description: '', location: '' });
      loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project: ' + error.message);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      const { id, name, description, location } = editing;
      await projectsApi.update(id, { name, description, location });
      setEditing(null);
      loadProjects();
    } catch (error) {
      alert('Failed to update project: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await projectsApi.remove(deleting.project.id, { force: deleting.needsForce });
      setDeleting(null);
      loadProjects();
    } catch (error) {
      if (error.response?.status === 409) {
        setDeleting({ ...deleting, needsForce: true, error: error.response?.data?.detail || 'Project still has datasets.' });
      } else {
        setDeleting({ ...deleting, error: error.response?.data?.detail || error.message });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!selectedOrgId) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumbs />
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Select or create an organization to see projects</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Projects</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            data-testid="create-project-button"
          >
            Create Project
          </button>
        </div>

        {showCreateForm && (
          <div className="mb-6 bg-card border border-border rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Create New Project</h2>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Project Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <input
                  type="text"
                  value={newProject.location}
                  onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g., Site coordinates or region"
                  data-testid="project-location-input"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
                  data-testid="submit-project-button"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-border rounded-md hover:bg-muted"
                  data-testid="cancel-project-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(project => (
            <div
              key={project.id}
              className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition"
              data-testid={`project-card-${project.id}`}
            >
              <h3 className="text-lg font-semibold mb-2">{project.name}</h3>
              {project.description && (
                <p className="text-sm text-muted-foreground mb-4">{project.description}</p>
              )}
              {project.location && (
                <p className="text-sm text-muted-foreground mb-4">📍 {project.location}</p>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  Created {new Date(project.created_at).toLocaleDateString()}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditing({ id: project.id, name: project.name || '', description: project.description || '', location: project.location || '' })}
                    className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-muted"
                    data-testid={`edit-project-${project.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleting({ project, needsForce: false, error: '' })}
                    className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                    data-testid={`delete-project-${project.id}`}
                  >
                    Delete
                  </button>
                  <Link
                    to={`/map?project=${project.id}`}
                    className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 transition"
                    data-testid={`project-map-link-${project.id}`}
                  >
                    🗺️ View on Map
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" role="dialog" aria-label="Edit project">
            <form onSubmit={handleSaveEdit} className="bg-card border border-border rounded-lg p-6 w-full max-w-md space-y-4" data-testid="edit-project-form">
              <h2 className="text-lg font-semibold">Edit Project</h2>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="edit-project-name">Project Name</label>
                <input id="edit-project-name" type="text" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-3 py-2 border border-input rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="edit-project-description">Description</label>
                <textarea id="edit-project-description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-3 py-2 border border-input rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="edit-project-location">Location</label>
                <input id="edit-project-location" type="text" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="w-full px-3 py-2 border border-input rounded-md" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-border rounded-md hover:bg-muted">Cancel</button>
                <button type="submit" disabled={busy} className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50" data-testid="save-project-button">Save</button>
              </div>
            </form>
          </div>
        )}

        {deleting && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" role="alertdialog" aria-label="Delete project" data-testid="delete-project-dialog">
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md space-y-4">
              <h2 className="text-lg font-semibold">Delete project?</h2>
              <p className="text-sm">
                Delete <strong>{deleting.project.name}</strong>? This cannot be undone.
              </p>
              {deleting.error && <p className="text-sm text-red-600" data-testid="delete-project-error">{deleting.error}</p>}
              {deleting.needsForce && (
                <p className="text-sm text-red-600">Confirming again will also permanently delete all of its datasets.</p>
              )}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setDeleting(null)} className="px-4 py-2 border border-border rounded-md hover:bg-muted">Cancel</button>
                <button type="button" disabled={busy} onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:opacity-90 disabled:opacity-50" data-testid="confirm-delete-project">
                  {deleting.needsForce ? 'Delete project and datasets' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {projects.length === 0 && !showCreateForm && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No projects yet</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Create Your First Project
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Projects;
